// frontend/src/components/cohesion/EntityDrawer.Event.tsx
// Generic calendar-event panel — used when a CalendarEvent is clicked and
// its underlying entity isn't a production / person / space we'd open directly.

import { useEffect, useState } from 'react'
import { useLanguage } from '../../i18n/index.jsx'
import api from '../../utils/api.js'

type Lang = { t: (k: string) => string }

interface Props {
  id: number
  navigate: (to: string) => void
  closeDrawer: () => void
}

interface CalendarEvent {
  id: number
  subject_type: string
  title: string
  starts_at: string
  ends_at: string
  status?: string
  production_id?: number | null
  person_id?: number | null
}

export default function EntityDrawerEvent({ id, navigate, closeDrawer }: Props) {
  const { t } = useLanguage() as Lang
  const [data, setData] = useState<CalendarEvent | null>(null)
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api.get<CalendarEvent>(`/calendar/events/${id}`)
      .then((r) => { if (!cancelled) setData(r.data) })
      .catch((e) => { if (!cancelled) setError(String(e?.response?.data?.error ?? e?.message ?? 'load failed')) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  if (loading) return <div className="entity-drawer-loading">{t('loading')}</div>
  if (error)   return <div className="entity-drawer-error" role="alert">{error}</div>
  if (!data)   return <div className="entity-drawer-empty">{t('itemNotFound')}</div>

  const start = new Date(data.starts_at)
  const end   = new Date(data.ends_at)
  const sameDay = start.toDateString() === end.toDateString()

  return (
    <>
      <div className="entity-drawer-title-link" aria-disabled="true">{data.title}</div>
      <div className="entity-drawer-meta">
        <div className="entity-drawer-meta-row">
          <span className="entity-drawer-meta-key">{t('type')}</span>
          <span className="entity-drawer-meta-val">{data.subject_type}</span>
        </div>
        <div className="entity-drawer-meta-row">
          <span className="entity-drawer-meta-key">{t('when')}</span>
          <span className="entity-drawer-meta-val">
            {sameDay
              ? `${start.toLocaleString()} – ${end.toLocaleTimeString()}`
              : `${start.toLocaleString()} – ${end.toLocaleString()}`}
          </span>
        </div>
        {data.status && (
          <div className="entity-drawer-meta-row">
            <span className="entity-drawer-meta-key">{t('status')}</span>
            <span className="entity-drawer-meta-val">{data.status}</span>
          </div>
        )}
      </div>
      <div className="entity-drawer-actions">
        {data.production_id && (
          <button type="button" className="entity-drawer-action" onClick={() => { closeDrawer(); navigate(`/productions/${data.production_id}`) }}>
            {t('openWorkspace')}
          </button>
        )}
        {data.person_id && (
          <button type="button" className="entity-drawer-action" onClick={() => { closeDrawer(); navigate(`/people?tab=availability&user=${data.person_id}`) }}>
            {t('navAvailability')}
          </button>
        )}
      </div>
    </>
  )
}
